import { useState, type DragEvent, type ChangeEvent } from 'react';
import { Modal } from './Modal';
import { OrgPicker } from './OrgPicker';
import { AddressFields } from './AddressFields';
import { PhotoCropModal } from './PhotoCropModal';
import { LookupSelect } from './LookupSelect';
import { useParseVcards, useApplyVcards, type VcardFileInput } from '../api/queries/contactsIngest';
import { useLookups, activeOptionsFor } from '../api/queries/lookups';
import { ApiError } from '../api/client';
import {
  addressValueFromColumns, addressValueToColumns, type AddressValue,
} from '../utils/address';
import type { VcardApplyItem, VcardCardResult } from '@stfors/shared-types';
import './VcardImportModal.css';
import { Toggle } from './Toggle';

interface VcardImportModalProps {
  open: boolean;
  onClose: () => void;
}

type RoleChoice = '' | 'client' | 'vendor' | 'contractor';

const PHOTO_MIMES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

// A cropped File (image/jpeg from the cropper) → the { base64, mime } shape the
// apply payload + row state use.
function fileToPhoto(file: File): Promise<{ base64: string; mime: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const res = String(reader.result);
      resolve({ base64: res.slice(res.indexOf(',') + 1), mime: file.type || 'image/jpeg' });
    };
    reader.readAsDataURL(file);
  });
}

// The reverse: an existing row photo → a File, so the cropper can be seeded
// from the photo the vCard already carried (re-crop without re-picking).
function photoToFile(photo: { base64: string; mime: string }): File {
  const bin = atob(photo.base64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], 'photo', { type: photo.mime });
}

interface ReviewRow {
  index: number;
  source: string;
  personMatchId: number | null;
  personMatchName: string | null;
  orgMatchName: string | null;
  personMode: 'existing' | 'create';
  title: string; first_name: string; last_name: string; suffix: string; nickname: string;
  orgMode: 'existing' | 'create' | 'none';
  orgExistingId: number | null;
  orgCreateName: string;
  contactTitle: string;
  isPrimary: boolean;
  role: RoleChoice;
  roleEmail: string;
  addr: AddressValue;
  phones: { phone_type: string; phone: string }[];
  emails: { label: string | null; email: string }[];
  photo: { base64: string; mime: string } | null;
}

function toRow(card: VcardCardResult): ReviewRow {
  const p = card.parsed!;
  return {
    index: card.index,
    source: card.source_filename,
    personMatchId: card.person_match?.person_id ?? null,
    personMatchName: card.person_match?.display_name ?? null,
    orgMatchName: card.org_match?.name ?? null,
    personMode: card.person_match ? 'existing' : 'create',
    title: p.prefix ?? '',
    first_name: p.first_name ?? '',
    last_name: p.last_name ?? '',
    suffix: p.suffix ?? '',
    nickname: p.nickname ?? '',
    orgMode: card.org_match ? 'existing' : p.org_name ? 'create' : 'none',
    orgExistingId: card.org_match?.organization_id ?? null,
    orgCreateName: p.org_name ?? '',
    contactTitle: p.contact_title ?? '',
    isPrimary: false,
    role: '',
    roleEmail: p.emails[0]?.email ?? '',
    addr: addressValueFromColumns(p.address),
    phones: p.phones,
    emails: p.emails,
    photo: p.photo,
  };
}

export function VcardImportModal({ open, onClose }: VcardImportModalProps): JSX.Element {
  const parse = useParseVcards();
  const apply = useApplyVcards();
  const lookups = useLookups();
  const titleOptions = activeOptionsFor(lookups.data?.lookup_options, 'title');
  const suffixOptions = activeOptionsFor(lookups.data?.lookup_options, 'suffix');
  const [phase, setPhase] = useState<'drop' | 'review'>('drop');
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [skipped, setSkipped] = useState<VcardCardResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // Per-row photo crop: which row, and the picked file awaiting crop.
  const [cropRow, setCropRow] = useState<number | null>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);

  const reset = () => { setPhase('drop'); setRows([]); setSkipped([]); setError(null); };
  const close = () => { reset(); onClose(); };

  const ingest = async (fileList: FileList | File[]) => {
    setError(null);
    const files = Array.from(fileList).filter((f) => /\.vcf$/i.test(f.name) || f.type === 'text/vcard');
    if (files.length === 0) { setError('Drop one or more .vcf files.'); return; }
    try {
      const inputs: VcardFileInput[] = await Promise.all(
        files.map(async (f) => ({ filename: f.name, content: await f.text() })),
      );
      const res = await parse.mutateAsync(inputs);
      setRows(res.cards.filter((c) => c.parsed).map(toRow));
      setSkipped(res.cards.filter((c) => c.skipped));
      setPhase('review');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not read the dropped files.');
    }
  };

  const patch = (index: number, p: Partial<ReviewRow>) =>
    setRows((rs) => rs.map((r) => (r.index === index ? { ...r, ...p } : r)));

  const onPickPhoto = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!f) return;
    if (!PHOTO_MIMES.includes(f.type)) { setError('Choose a JPEG, PNG, GIF, or WebP image.'); return; }
    setError(null);
    setCropRow(index);
    setCropFile(f);
  };

  // Re-crop the photo the row already has (seed the cropper from it).
  const onEditPhoto = (index: number, photo: { base64: string; mime: string }) => {
    setError(null);
    setCropRow(index);
    setCropFile(photoToFile(photo));
  };

  const onCropConfirm = async (cropped: File) => {
    if (cropRow === null) return;
    patch(cropRow, { photo: await fileToPhoto(cropped) });
    setCropFile(null);
    setCropRow(null);
  };

  const onApply = async () => {
    setError(null);
    // Validate the editable choices before building the payload.
    for (const r of rows) {
      if (r.personMode === 'create' && !(r.first_name.trim() || r.last_name.trim())) {
        setError(`"${r.source}": a new person needs a first or last name.`); return;
      }
      if (r.orgMode === 'existing' && r.orgExistingId === null) {
        setError(`"${r.source}": pick a company or switch it to "Create new" / "None".`); return;
      }
      if (r.orgMode === 'create' && !r.orgCreateName.trim()) {
        setError(`"${r.source}": enter the company name.`); return;
      }
      if (r.role === 'contractor' && !r.roleEmail.trim()) {
        setError(`"${r.source}": a contractor role needs an email.`); return;
      }
    }
    const items: VcardApplyItem[] = rows.map((r) => {
      const cols = addressValueToColumns(r.addr);
      return {
        person: r.personMode === 'existing' ? { mode: 'existing', person_id: r.personMatchId! } : { mode: 'create' },
        title: r.title.trim() || null,
        first_name: r.first_name.trim() || null,
        last_name: r.last_name.trim() || null,
        suffix: r.suffix.trim() || null,
        preferred_name: r.nickname.trim() || null,
        phones: r.phones,
        emails: r.emails,
        address: cols.line1 || cols.city || cols.postal_code ? cols : null,
        photo: r.photo,
        organization: r.orgMode === 'existing'
          ? { mode: 'existing', organization_id: r.orgExistingId! }
          : r.orgMode === 'create'
            ? { mode: 'create', name: r.orgCreateName.trim() }
            : { mode: 'none' },
        contact_title: r.contactTitle.trim() || null,
        is_primary_contact: r.isPrimary,
        role: r.role || null,
        role_email: r.role === 'contractor' ? (r.roleEmail.trim() || null) : null,
      };
    });
    try {
      await apply.mutateAsync({ items });
      close();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not import the contacts.');
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) void ingest(e.dataTransfer.files);
  };

  return (
    <Modal title="Import Contacts (.vcf)" open={open} onClose={close} busy={parse.isPending || apply.isPending || cropFile !== null}>
      {phase === 'drop' ? (
        <div className="vim-body">
          <div
            className={`vim-dropzone ${dragOver ? 'vim-dropzone-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
          >
            <p>Drag &amp; drop <code>.vcf</code> contact cards here</p>
            <p className="vim-muted">or</p>
            <label className="btn-cyan-primary vim-pick">
              Choose files
              <input
                type="file" accept=".vcf,text/vcard" multiple style={{ display: 'none' }}
                onChange={(e) => { if (e.target.files) void ingest(e.target.files); e.target.value = ''; }}
              />
            </label>
            {parse.isPending && <p className="vim-muted">Parsing…</p>}
          </div>
          {error && <p className="vim-error" role="alert">{error}</p>}
        </div>
      ) : (
        <div className="vim-body">
          <p className="vim-muted">
            Review each contact, choose its role and company, then import.
            {skipped.length > 0 && ` ${skipped.length} card(s) skipped (no contact details).`}
          </p>
          {rows.length === 0 && <p className="vim-error">No contact cards found in those files.</p>}

          {rows.map((r) => (
            <div key={r.index} className="vim-card">
              <div className="vim-card-head">
                <div className="vim-photo-block">
                  {r.photo
                    ? (
                      <button
                        type="button"
                        className="vim-photo-edit"
                        onClick={() => onEditPhoto(r.index, r.photo!)}
                        title="Crop this photo"
                        aria-label="Crop this photo"
                      >
                        <img className="vim-photo" alt="" src={`data:${r.photo.mime};base64,${r.photo.base64}`} />
                      </button>
                    )
                    : <span className="vim-photo vim-photo-empty" aria-hidden="true" />}
                  <div className="vim-photo-actions">
                    <label className="vim-photo-btn">
                      {r.photo ? 'Change' : 'Add photo'}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp"
                        hidden
                        onChange={(e) => onPickPhoto(r.index, e)}
                      />
                    </label>
                    {r.photo && (
                      <button type="button" className="vim-photo-btn" onClick={() => patch(r.index, { photo: null })}>
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                <strong>{[r.title, r.first_name, r.last_name, r.suffix].filter(Boolean).join(' ') || '(no name)'}</strong>
                <span className="vim-src">{r.source}</span>
              </div>

              {/* Person target */}
              <div className="vim-field">
                <span>Person</span>
                {r.personMatchId !== null ? (
                  <select className="form-input" value={r.personMode} onChange={(e) => patch(r.index, { personMode: e.target.value as ReviewRow['personMode'] })}>
                    <option value="existing">Merge into existing — {r.personMatchName}</option>
                    <option value="create">Create new person</option>
                  </select>
                ) : <span className="vim-muted">New person</span>}
              </div>
              {r.personMode === 'create' && (
                <div className="vim-row">
                  <select className="form-input" value={r.title} onChange={(e) => patch(r.index, { title: e.target.value })} aria-label="Title">
                    <option value="">Title</option>
                    {titleOptions.map((o) => (<option key={o.id} value={o.value}>{o.value}</option>))}
                    {r.title && !titleOptions.some((o) => o.value === r.title) && <option value={r.title}>{r.title} (legacy)</option>}
                  </select>
                  <input className="form-input" placeholder="First" value={r.first_name} onChange={(e) => patch(r.index, { first_name: e.target.value })} />
                  <input className="form-input" placeholder="Last" value={r.last_name} onChange={(e) => patch(r.index, { last_name: e.target.value })} />
                  <select className="form-input" value={r.suffix} onChange={(e) => patch(r.index, { suffix: e.target.value })} aria-label="Suffix">
                    <option value="">Suffix</option>
                    {suffixOptions.map((o) => (<option key={o.id} value={o.value}>{o.value}</option>))}
                    {r.suffix && !suffixOptions.some((o) => o.value === r.suffix) && <option value={r.suffix}>{r.suffix} (legacy)</option>}
                  </select>
                </div>
              )}

              {/* Company */}
              <div className="vim-field">
                <span>Company</span>
                <select className="form-input" value={r.orgMode} onChange={(e) => patch(r.index, { orgMode: e.target.value as ReviewRow['orgMode'] })}>
                  <option value="existing">Use existing company</option>
                  <option value="create">Create new{r.orgCreateName ? ` — "${r.orgCreateName}"` : ''}</option>
                  <option value="none">No company</option>
                </select>
              </div>
              {r.orgMode === 'existing' && (
                <OrgPicker value={r.orgExistingId} onChange={(id) => patch(r.index, { orgExistingId: id })} />
              )}
              {r.orgMode === 'create' && (
                <input className="form-input" placeholder="Company name" value={r.orgCreateName} onChange={(e) => patch(r.index, { orgCreateName: e.target.value })} />
              )}
              {r.orgMode !== 'none' && (
                <div className="vim-row">
                  <input className="form-input" placeholder="Title at company (e.g. Partner)" value={r.contactTitle} onChange={(e) => patch(r.index, { contactTitle: e.target.value })} />
                  <Toggle
                    checked={r.isPrimary}
                    onChange={(v) => patch(r.index, { isPrimary: v })}
                    label="Primary contact"
                  />
                </div>
              )}

              {/* Role */}
              <div className="vim-field">
                <span>Role</span>
                <LookupSelect
                  category="contact_role"
                  value={r.role}
                  onChange={(v) => patch(r.index, { role: v as RoleChoice })}
                  className="form-input"
                  allowEmpty
                  emptyLabel="None — directory contact only"
                  aria-label="Role"
                />
              </div>
              {r.role === 'contractor' && (
                <input className="form-input" placeholder="Contractor email (required)" value={r.roleEmail} onChange={(e) => patch(r.index, { roleEmail: e.target.value })} />
              )}

              {/* Address */}
              <div className="vim-field">
                <span>Address</span>
                <AddressFields value={r.addr} onChange={(a) => patch(r.index, { addr: a })} idPrefix={`Card ${r.index}`} />
              </div>

              {(r.phones.length > 0 || r.emails.length > 0) && (
                <div className="vim-contacts">
                  {r.phones.map((p, i) => <span key={`p${i}`} className="vim-chip">{p.phone_type}: {p.phone}</span>)}
                  {r.emails.map((e, i) => <span key={`e${i}`} className="vim-chip">{e.email}</span>)}
                </div>
              )}
            </div>
          ))}

          {error && <p className="vim-error" role="alert">{error}</p>}
          <div className="vim-actions">
            <button type="button" className="btn-charcoal-secondary" onClick={reset} disabled={apply.isPending}>Back</button>
            <button type="button" className="btn-cyan-primary" onClick={onApply} disabled={apply.isPending || rows.length === 0}>
              {apply.isPending ? 'Importing…' : `Import ${rows.length} contact${rows.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}

      {cropFile && (
        <PhotoCropModal
          file={cropFile}
          open
          onCancel={() => { setCropFile(null); setCropRow(null); }}
          onConfirm={onCropConfirm}
        />
      )}
    </Modal>
  );
}
